import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import {
  Home, User, Calendar, FileText, CreditCard, Bell, Store, LogOut, Menu, X,
} from "lucide-react";
import "./Vendornav.css";

// Full set — used by the desktop sidebar, which has room for everything.
const NAV_ITEMS = [
  { to: "/vendor/dashboard",     label: "Home",          icon: Home },
  { to: "/vendor/events",        label: "Events",        icon: Calendar },
  { to: "/vendor/Account",       label: "Profile",       icon: User },
  { to: "/vendor/products",      label: "My Shop",       icon: Store }, // <-- Activated
  { to: "/vendor/documents",     label: "Documents",     icon: FileText },
  { to: "/vendor/payments",      label: "Payments",      icon: CreditCard },
  { to: "/vendor/notifications", label: "Notifications", icon: Bell },
];

// Mobile bottom tab bar only has room for what vendors reach for most —
// everything else moves into the top bar (Notifications) or the hamburger
// menu (Documents, Payments, Marketplace, Logout) instead of a scrolling
// icon strip.
const PRIMARY_NAV_ITEMS = [
  { to: "/vendor/dashboard", label: "Home",    icon: Home },
  { to: "/vendor/events",    label: "Events",  icon: Calendar },
  { to: "/vendor/Account",   label: "Profile", icon: User },
];

// Secondary items, reachable via the mobile hamburger menu only.
const MENU_NAV_ITEMS = [
  { to: "/vendor/documents", label: "Documents", icon: FileText },
  { to: "/vendor/payments",  label: "Payments",  icon: CreditCard },
];

const MARKETPLACE_ITEM = { label: "Marketplace", icon: Store };

const VendorNav = ({ vendor }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      {/* ── Desktop sidebar — unchanged, full nav ── */}
      <nav className="vn-sidebar">
        <div className="vn-brand">
          {vendor?.logo_url ? (
            <img src={vendor.logo_url} alt={vendor?.brand_name || "Vendor logo"} className="vn-brand-logo" />
          ) : (
            <div className="vn-brand-logo-placeholder">
              {vendor?.brand_name?.[0]?.toUpperCase() || "?"}
            </div>
          )}
          <div className="vn-brand-text">
            <span className="vn-brand-name">{vendor?.brand_name || "Vendor"}</span>
            <span className="vn-brand-sub">Vendor portal</span>
          </div>
        </div>

        <div className="vn-links">
          {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => `vn-link ${isActive ? "vn-link--active" : ""}`}
            >
              <Icon size={17} />
              <span>{label}</span>
            </NavLink>
          ))}

          <div className="vn-link vn-link--disabled" aria-disabled="true">
            <MARKETPLACE_ITEM.icon size={17} />
            <span>{MARKETPLACE_ITEM.label}</span>
            <span className="vn-soon-tag">Soon</span>
          </div>
        </div>

        <button className="vn-logout" onClick={handleLogout}>
          <LogOut size={16} />
          <span>Log out</span>
        </button>
      </nav>

      {/* ── Mobile top bar — brand, notifications, hamburger ── */}
      <div className="vn-mobile-topbar">
        <div className="vn-mobile-brand">
          {vendor?.logo_url ? (
            <img
              src={vendor.logo_url}
              alt={vendor?.brand_name || "Vendor logo"}
              className="vn-brand-logo vn-brand-logo--sm"
            />
          ) : (
            <div className="vn-brand-logo-placeholder vn-brand-logo-placeholder--sm">
              {vendor?.brand_name?.[0]?.toUpperCase() || "?"}
            </div>
          )}
          <span className="vn-mobile-brand-name">{vendor?.brand_name || "Vendor"}</span>
        </div>

        <div className="vn-mobile-topbar-actions">
          <NavLink
            to="/vendor/notifications"
            className={({ isActive }) => `vn-mobile-icon-btn ${isActive ? "vn-mobile-icon-btn--active" : ""}`}
            aria-label="Notifications"
          >
            <Bell size={19} />
          </NavLink>
          <button
            type="button"
            className="vn-mobile-icon-btn"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
            aria-expanded={menuOpen}
          >
            <Menu size={20} />
          </button>
        </div>
      </div>

      {/* ── Mobile bottom tab bar — Home / Events / Profile only ── */}
      <nav className="vn-bottom-bar" aria-label="Primary">
        {PRIMARY_NAV_ITEMS.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) => `vn-bottom-link ${isActive ? "vn-bottom-link--active" : ""}`}
          >
            <Icon size={20} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      {/* ── Mobile hamburger menu — Documents, Payments, Marketplace, Logout ── */}
      {menuOpen && (
        <div className="vn-menu-backdrop" onClick={closeMenu}>
          <div className="vn-menu-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="vn-menu-header">
              <span>Menu</span>
              <button
                type="button"
                className="vn-mobile-icon-btn"
                onClick={closeMenu}
                aria-label="Close menu"
              >
                <X size={20} />
              </button>
            </div>

            <div className="vn-menu-links">
              {MENU_NAV_ITEMS.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={closeMenu}
                  className={({ isActive }) => `vn-link ${isActive ? "vn-link--active" : ""}`}
                >
                  <Icon size={17} />
                  <span>{label}</span>
                </NavLink>
              ))}

              <div className="vn-link vn-link--disabled" aria-disabled="true">
                <MARKETPLACE_ITEM.icon size={17} />
                <span>{MARKETPLACE_ITEM.label}</span>
                <span className="vn-soon-tag">Soon</span>
              </div>
            </div>

            <button
              type="button"
              className="vn-logout vn-logout--menu"
              onClick={() => { closeMenu(); handleLogout(); }}
            >
              <LogOut size={16} />
              <span>Log out</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default VendorNav;